'use client';

import { useEffect, useMemo, useState } from 'react';

import { useGlobalContext } from '@/store/contexts/global-context';
import { useIntersectionObserver } from '@/hooks/use-intersection-observer';
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion';
import { AMPLITUDE_EXPERIMENTS, getExperimentValue } from '@/utils/experiments';
import {
    ROTATION_INTERVAL_MS,
    ROTATION_MODE,
    pickLaunchIndex,
    resolveMode,
    type Transition
} from './utils/rotating-zone-title-utils';

type RotatingZoneTitleProps = {
    titles: string[];
    zoneKey?: string;
};

export const RotatingZoneTitle = ({ titles, zoneKey }: RotatingZoneTitleProps) => {
    const { experiments } = useGlobalContext();
    const prefersReducedMotion = usePrefersReducedMotion();
    const [ref, isIntersecting] = useIntersectionObserver<HTMLSpanElement>();
    const [index, setIndex] = useState(0);
    const [transition, setTransition] = useState<Transition | null>(null);

    const variant = useMemo(
        () => getExperimentValue(experiments, AMPLITUDE_EXPERIMENTS.ROTATING_ZONE_TITLE),
        [experiments]
    );
    const mode = useMemo(() => resolveMode(variant, prefersReducedMotion), [variant, prefersReducedMotion]);

    useEffect(() => {
        if (mode !== ROTATION_MODE.LAUNCH) {
            return;
        }

        setIndex(pickLaunchIndex(titles, zoneKey));
    }, [mode, titles, zoneKey]);

    useEffect(() => {
        if (mode !== ROTATION_MODE.TIMED || !isIntersecting) {
            return undefined;
        }
        const intervalId = window.setInterval(() => {
            setIndex((current) => {
                const next = (current + 1) % titles.length;

                setTransition({ from: current, direction: next === 0 ? 'up' : 'down' });

                return next;
            });
        }, ROTATION_INTERVAL_MS);

        return () => window.clearInterval(intervalId);
    }, [mode, isIntersecting, titles.length]);

    const safeIndex = index % titles.length;
    const outgoing =
        transition && transition.from % titles.length !== safeIndex
            ? { index: transition.from % titles.length, direction: transition.direction }
            : null;

    return (
        <span
            ref={mode === ROTATION_MODE.TIMED ? ref : undefined}
            className="rotating-zone-title"
            data-testid="rotating-zone-title">
            {outgoing && (
                <span
                    key={`out-${outgoing.index}`}
                    className={`rotating-zone-title__text rotating-zone-title__text--out-${outgoing.direction}`}
                    aria-hidden="true"
                    onAnimationEnd={() => setTransition(null)}>
                    {titles[outgoing.index]}
                </span>
            )}
            <span
                key={`in-${safeIndex}`}
                className={`rotating-zone-title__text${outgoing ? ` rotating-zone-title__text--in-${outgoing.direction}` : ''}`}
                data-testid="rotating-zone-title-value">
                {titles[safeIndex]}
            </span>
        </span>
    );
};
